import { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment/min/moment-with-locales';
import { Modal, Row, Col, Form } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import MemberInfoCard from '~/components/Card/MemberInfoCard.jsx';
import { CurrentUserGroupsWidget } from '~/components/GenericForm/Widgets/UserGroupWidget';
import AlertMessage, { createAlert } from '~/components/Alert/AlertMessage.jsx';
import Radio from '~/components/CustomRadio/CustomRadio.jsx';

//elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import { Input, NumberInput } from '../../../elements/Input.js';
import CustomMultiSelect from '../../../elements/CustomMultiSelect';
import StatPill from '../../../elements/StatPill.js';

//utils
import { totalTimeFormat } from '../../../utils/dateFormat';
import { overtime_transaction_type_map, user_type_map } from '../../../utils/normalStatuses.js';
import { OVERTIME_PAID_HOURS_HARD_LIMIT_MINS, OVERTIME_PAID_HOURS_SOFT_LIMIT_MINS } from '../../../utils/constants.js';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import SingleMemberIcon from '~/assets/img/addUser.svg';
import BulkMembersIcon from '~/assets/img/profile-2user.svg';

const AddPaidOvertimeModal = ({ userDetails, onClose, onSaved, overtimeBankStore, commonStore, userStore, t }) => {
    const overtimeBankSettings = commonStore.config?.client?.data?.basicRules?.overtimeBankSettings || {};
    const allowAllEmployees = !!overtimeBankSettings.enablePaidOvertimeBalanceForAllEmployees;
    const defaultBalanceMinutes = overtimeBankSettings.defaultPaidOvertimeBalance || 0;
    const currentUser = userStore.currentUser;
    // Mirrors basicRules.preventPmSelfApproval's existing enforcement on timesheet/absence/
    // vacation approvals - when enabled, a PM can't manage their own Paid Overtime Balance either
    // (see middleware/overtimeBank.js's updatePaidOvertimeBalance, which is the actual guard;
    // this only pre-empts the request so the PM doesn't fill out the whole form first).
    const preventPmSelfApproval = !!commonStore.config?.client?.data?.basicRules?.preventPmSelfApproval;
    // Paid overtime can only ever be added for the current year (see middleware/overtimeBank.js's
    // updatePaidOvertimeBalance guard) - bound the picker to match so the request never gets there.
    const currentYear = moment().year();
    const minForDate = `${currentYear}-01-01`;
    const maxForDate = `${currentYear}-12-31`;

    const [mode, setMode] = useState('single');
    const [direction, setDirection] = useState('credit');
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [groupCode, setGroupCode] = useState(null);
    const [hoursPart, setHoursPart] = useState(Math.floor(defaultBalanceMinutes / 60));
    const [minutesPart, setMinutesPart] = useState(defaultBalanceMinutes % 60);
    const [forDate, setForDate] = useState(moment().format('YYYY-MM-DD'));
    const [reason, setReason] = useState(t('Correction'));
    const [saving, setSaving] = useState(false);
    const [eligibilityAlerts, setEligibilityAlerts] = useState([]);
    const [syncProfileDefault, setSyncProfileDefault] = useState(false);
    const [syncMode, setSyncMode] = useState('replace');

    const isSelfApprovalBlocked = (user) => preventPmSelfApproval && currentUser?.user_type === 'pm' && user?.id === currentUser?.id;
    const isEligible = (user) => (allowAllEmployees || user?.payment_mode === 'fixed') && !isSelfApprovalBlocked(user);

    const currentTotals = overtimeBankStore.currentMemberList.find(m => m.id === userDetails?.id) || {
        total_hours_in_mins: userDetails?.total_hours_in_mins || 0,
        paid_overtime_balance_in_mins: userDetails?.paid_overtime_balance_in_mins || 0,
        paid_overtime_renewal_hours: userDetails?.paid_overtime_renewal_hours || 0,
    };

    const bulkMemberOptions = overtimeBankStore.currentMemberList
        .filter(isEligible)
        .filter(m => !groupCode || m.user_groups?.includes(groupCode))
        .map(m => ({ value: m.id, label: m.name }));

    const totalMinutes = (Number(hoursPart) || 0) * 60 + (Number(minutesPart) || 0);
    const signedMinutes = direction === 'debit' ? -totalMinutes : totalMinutes;
    const resultingBalance = mode === 'single' ? (currentTotals.paid_overtime_balance_in_mins || 0) + signedMinutes : null;
    const singleModeEligible = mode !== 'single' || isEligible(userDetails);
    const dateTimeRules = commonStore.getDateTimeRules();
    const memberInfoLines = [
        `${t('Emp. Id.')}: ${userDetails?.internal_number || ''}`,
        userDetails?.user_type ? t(user_type_map[userDetails.user_type] || userDetails.user_type) : '',
    ];

    const enteredHours = totalMinutes / 60;
    const replacePreviewHours = enteredHours;
    const addPreviewHours = mode === 'single' ? (currentTotals.paid_overtime_renewal_hours || 0) + enteredHours : null;

    useEffect(() => {
        if (singleModeEligible) {
            setEligibilityAlerts([]);
        } else {
            const message = isSelfApprovalBlocked(userDetails)
                ? t('You cannot manage your own Paid Overtime Balance. Please contact another PM or Administrator')
                : t('This employee is not eligible for paid overtime balance management. Please contact your Administrator');
            setEligibilityAlerts([createAlert(message, 'warning')]);
        }
    }, [singleModeEligible]);

    useEffect(() => {
        if (direction !== 'credit') setSyncProfileDefault(false);
    }, [direction]);

    const exceedsHardLimit = totalMinutes > OVERTIME_PAID_HOURS_HARD_LIMIT_MINS;
    const exceedsSoftLimit = totalMinutes > OVERTIME_PAID_HOURS_SOFT_LIMIT_MINS;

    const isSaveDisabled = saving
        || totalMinutes === 0
        || !singleModeEligible
        || exceedsHardLimit
        || (mode === 'single' && resultingBalance < 0)
        || (mode === 'bulk' && !selectedUserIds.length);

    const submitSave = async () => {
        const userIds = mode === 'single' ? [userDetails.id] : selectedUserIds;
        setSaving(true);
        try {
            const result = await overtimeBankStore.updatePaidOvertimeBalance({
                userIds,
                overtime_hours_in_mins: signedMinutes,
                for_date: forDate,
                reason,
                updateProfileDefault: direction === 'credit' && syncProfileDefault ? syncMode : null,
            });
            if (result.updatedUserIds?.length) {
                commonStore.addNotification(t('Saved'), null, 'success');
                overtimeBankStore.resetLastListLoadTime(new Date());
                if (onSaved) onSaved();
            }
            if (result.error?.length) {
                commonStore.addNotification(result.error.map(e => e.reason).join(', '), null, 'error');
            } else {
                onClose();
            }
        } catch (error) {
            console.error(error);
            commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (mode === 'single' && !userDetails?.id) {
            return commonStore.addNotification(t('No member selected'), null, 'error');
        }
        if (exceedsHardLimit) {
            return commonStore.addNotification(t('Entered hours exceed the maximum allowed paid overtime per year and cannot be submitted'), null, 'error');
        }

        confirmAlert({
            title: t('Confirm Paid Overtime'),
            message: (
                <div>
                    {exceedsSoftLimit &&
                        <p className='fw-bold text-danger'>
                            {t('You are asking the employee to work beyond the standards defined by the EU labour guidelines')}
                        </p>
                    }
                    <ul className='ps-3 mb-0 text-start'>
                        <li>{t('You are sure that these amounts are paid to the employee and the employee has accepted to work overtime against these hours')}</li>
                        <li>{t("The employee's overtime hours will not be synced by the system until the paid hour balance has exceeded")}</li>
                        <li>{t('This is a one-way transaction and is not revertible, you will need to add another transaction to cancel these balances')}</li>
                    </ul>
                </div>
            ),
            buttons: [
                { label: t('Yes'), onClick: () => submitSave() },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    return (
        <Modal dialogClassName="lg" className="lg" size="lg" show backdrop="static" onHide={onClose}>
            <Modal.Header className="set_ModalHeader">
                <Modal.Title>{t('Paid Overtime')}</Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" onClick={onClose} alt={'close-icon'} />
            </Modal.Header>
            <Modal.Body style={{ minHeight: 300 }}>
                <div className='tabContainer mb-3' style={{ overflow: 'hidden' }}>
                    <button
                        className={mode === 'single' ? "tablink tablink__active-left-doc" : "tablink tablink__left-doc"}
                        onClick={() => setMode('single')}
                    >
                        <img
                            src={SingleMemberIcon}
                            alt="single-member"
                            className="me-2"
                            style={{ height: 18, filter: mode === 'single' ? 'brightness(0) invert(1)' : 'none' }}
                        />
                        {t('Single Member')}
                    </button>
                    <button
                        className={mode === 'bulk' ? "tablink tablink__active-right-doc" : "tablink tablink__right-doc"}
                        onClick={() => setMode('bulk')}
                    >
                        <img
                            src={BulkMembersIcon}
                            alt="bulk-members"
                            className="me-2"
                            style={{ height: 18, filter: mode === 'bulk' ? 'brightness(0) invert(1)' : 'none' }}
                        />
                        {t('Bulk Members')}
                    </button>
                </div>
                {mode === 'single' ?
                    <>
                        <Row className="mb-3">
                            <Col md={12} className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                                <MemberInfoCard
                                    imageId={userDetails?.image ? userDetails?.image : false}
                                    name={userDetails?.name}
                                    lines={memberInfoLines}
                                />
                                <StatPill items={[
                                    { label: t('Overtime'), value: totalTimeFormat(currentTotals.total_hours_in_mins || 0, dateTimeRules) },
                                    { label: t('Paid Overtime'), value: totalTimeFormat(currentTotals.paid_overtime_balance_in_mins || 0, dateTimeRules) },
                                ]} />
                            </Col>
                        </Row>
                        {!singleModeEligible &&
                            <Row className="mb-2">
                                <Col>
                                    <AlertMessage messages={eligibilityAlerts} onClose={setEligibilityAlerts} maxLimit={1} />
                                </Col>
                            </Row>
                        }
                    </>
                    :
                    <Row className="mb-3">
                        <Col md={4}>
                            <label className='fw-600'>{t('Select Group')}</label>
                            <CurrentUserGroupsWidget
                                user_id={0}
                                value={groupCode}
                                onChange={(value) => {
                                    setGroupCode(value === -1 ? null : value);
                                    setSelectedUserIds([]);
                                }}
                            />
                        </Col>
                        <Col md={8}>
                            <label className='fw-600'>{t('Select Members')}</label>
                            {bulkMemberOptions.length ? <></> :
                                <span className='text-danger fw-600 font-size-10 ms-2'>({t('No data found')})</span>
                            }
                            <CustomMultiSelect
                                options={bulkMemberOptions}
                                value={selectedUserIds}
                                onChange={(ids) => setSelectedUserIds(ids)}
                                className={"custom-multi-select-max-height-150"}
                            />
                        </Col>
                    </Row>
                }
                <Row className="mb-3">
                    <Col md={6} className="d-flex gap-3">
                        <Radio
                            label={t(overtime_transaction_type_map.credit)}
                            checked={direction === 'credit'}
                            onChange={() => setDirection('credit')}
                        />
                        <Radio
                            label={t(overtime_transaction_type_map.debit)}
                            checked={direction === 'debit'}
                            onChange={() => setDirection('debit')}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col xs={6} md={3}>
                        <span className="text-muted fs12">{t('Hours')}(hh)</span>
                        <NumberInput
                            value={hoursPart}
                            min={0}
                            onChange={e => setHoursPart(Math.max(0, parseInt(e.target.value, 10) || 0))}
                        />
                    </Col>
                    <Col xs={6} md={3}>
                        <span className="text-muted fs12">{t('Minutes')}(mm)</span>
                        <NumberInput
                            value={minutesPart}
                            min={0}
                            onChange={e => setMinutesPart(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <span className="text-muted fs12">{t('For Date')}</span>
                        <input
                            type="date"
                            className="form-control"
                            value={forDate}
                            min={minForDate}
                            max={maxForDate}
                            onChange={e => setForDate(e.target.value)}
                        />
                    </Col>
                </Row>
                {direction === 'credit' && overtimeBankSettings.allowPaidOvertimeBalanceManagement && overtimeBankSettings.autoOvertimeBankRenewal &&
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Check
                                type="checkbox"
                                id="sync-profile-default-balance"
                                label={t("Also update employee's Paid Overtime Renewal hours")}
                                checked={syncProfileDefault}
                                disabled={totalMinutes === 0}
                                onChange={e => setSyncProfileDefault(e.target.checked)}
                            />
                        </Col>
                        {syncProfileDefault &&
                            <Col md={12} className="mt-2 d-flex flex-wrap gap-3">
                                <Radio
                                    label={`${t('Replace')} (${t('becomes')} ${replacePreviewHours.toFixed(2)} ${t('hours')})`}
                                    checked={syncMode === 'replace'}
                                    onChange={() => setSyncMode('replace')}
                                />
                                <Radio
                                    label={mode === 'single'
                                        ? `${t('Add')} (${t('becomes')} ${addPreviewHours.toFixed(2)} ${t('hours')})`
                                        : `${t('Add')} (${t('adds')} ${enteredHours.toFixed(2)} ${t('hours to each selected employee\'s current balance')})`}
                                    checked={syncMode === 'add'}
                                    onChange={() => setSyncMode('add')}
                                />
                            </Col>
                        }
                    </Row>
                }
                {mode === 'single' && resultingBalance < 0 &&
                    <Row className="mb-2">
                        <Col>
                            <span className="text-danger fs12">
                                {t('Paid overtime balance cannot be below 0')}
                            </span>
                        </Col>
                    </Row>
                }
                {exceedsHardLimit &&
                    <Row className="mb-2">
                        <Col>
                            <span className="text-danger fs12">
                                {t('Entered hours exceed the maximum allowed paid overtime per year and cannot be submitted')}
                            </span>
                        </Col>
                    </Row>
                }
                <Row className="mb-3">
                    <Col md={12}>
                        <span className="text-muted fs12">{t('Reason')}</span>
                        <Input
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" fill wd onClick={handleSave} disabled={isSaveDisabled}>
                    {t('Save')}
                </Button>
                <Button variant="secondary" wd className="ml-10" onClick={onClose}>
                    {t('Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default inject('overtimeBankStore', 'commonStore', 'userStore')(withLocalization(observer(AddPaidOvertimeModal)));